import { format } from "date-fns";
import React, { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";

import { faLinkSimple } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faEnvelope } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Alert, ButtonStandard, toast } from "@performyard/py-components";
import { Text as Typography } from "@performyard/py-components";

import { CopyToClipboard } from "../../../../../../components/CopyToClipboard";
import ContentModal from "../../../../../../components/Modal/Templates/ContentModal";
import Spacer from "../../../../../../components/Spacer";
import Tooltip from "../../../../../../components/Tooltip";

import { useSurveyEmailStatus } from "../../pages/SurveyResults/hooks/useSurveyEmailStatus";

import { SURVEY_DETAILS_MODALS } from "../../store/model";
import { useModal, useSetters, useSurveyLink } from "../../store/store";

import { useCurrentUser, useReScheduleSurveyEmail, useScheduleSurveyEmail } from "../../../../../../api/hooks";
import { useSurvey } from "../../hooks/useSurvey";
import { useSurveyAccessTier } from "../../hooks/useSurveyAccessTier";

export const ShareSurveyConfirmationModalContents = ({ surveyId }: { surveyId: string }) => {
	const setters = useSetters();
	const { survey } = useSurvey({ surveyId });

	const { data: currentUser } = useCurrentUser();

	const { emailScheduled, autoflowId } = useSurveyEmailStatus(surveyId);

	const { bodyText, futureDated } = useMemo(() => {
		const surveyStartDate = survey?.startDate ? new Date(survey.startDate) : null;
		const surveyEndDate = survey?.endDate ? new Date(survey.endDate) : null;
		const today = new Date();
		let bodyText = "";
		let futureDated = false;

		if (surveyStartDate && surveyStartDate < today && (!surveyEndDate || surveyEndDate > today)) {
			bodyText = "This survey is currently accepting responses";
			futureDated = false;
		} else if (surveyStartDate && surveyEndDate) {
			bodyText = `Survey Schedule: ${format(surveyStartDate, "MMMM d, yyyy")} - ${format(surveyEndDate, "MMMM d, yyyy")}`;
			futureDated = true;
		} else if (surveyStartDate) {
			bodyText = `Start Date: ${format(surveyStartDate, "MMMM d, yyyy")}`;
			futureDated = true;
		} else {
			bodyText = "Survey Schedule: Not scheduled";
			futureDated = false;
		}
		return { bodyText, futureDated };
	}, [survey?.startDate, survey?.endDate]);

	const { mutate: reScheduleSurvey } = useReScheduleSurveyEmail({
		onSuccess: () => {
			if (futureDated && survey?.startDate) {
				toast.success(`Your survey is scheduled for ${format(new Date(survey.startDate), "MMMM d, yyyy")}`);
			} else {
				toast.success("Your survey email is sending now");
			}
		},
	});

	const { mutate: createAutoflow } = useScheduleSurveyEmail({
		onSuccess: () => {
			if (futureDated && survey?.startDate) {
				toast.success(`Your survey is scheduled for ${format(new Date(survey.startDate), "MMMM d, yyyy")}`);
			} else {
				toast.success("Your survey email is sending now");
			}
		},
		onError: () => {
			toast.error("Error creating autoflow");
		},
	});

	const handleEmailLink = () => {
		if (survey?.startDate && currentUser?.me?.authorization?.payload?.user_id && !emailScheduled) {
			if (autoflowId) {
				// this is the case where an autoflow for this survey exists, but the email is no longer scheduled
				// if enabled, there are new participants eligible for the email, so we need to reschedule
				reScheduleSurvey({
					surveyId: surveyId,
				});
			} else {
				// this is the case where an autoflow does not exist, so we need to create one
				createAutoflow({
					surveyId: surveyId,
					urlPrefix: window.location.origin,
				});
			}
		} else {
			toast.error("Please set a start date for your survey");
		}
		setters.setModal(null);
	};
	return (
		<div className="flex w-full flex-col items-center">
			<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
				Email survey link to all eligible employees?
			</Typography>

			<Typography size="paragraph-2" color="neutral-9" weight="regular">
				<span className="block w-full text-center">{bodyText}</span>
			</Typography>

			<Spacer height={3} />
			{futureDated && (
				<>
					<Alert type="neutral" message="Emails for scheduled surveys are sent on the start date" />
					<Spacer height={2} />
				</>
			)}
			<div className="flex justify-center gap-2 pb-4">
				<div key="share-survey-cancel">
					<ButtonStandard size="large" type="button" onClick={() => setters.setModal(null)}>
						Cancel
					</ButtonStandard>
				</div>
				<div key="share-survey-submit">
					<ButtonStandard size="large" type="button" onClick={handleEmailLink} color="green">
						Yes, {futureDated ? "Schedule Email" : "Email Link"}
					</ButtonStandard>
				</div>
			</div>
		</div>
	);
};

const ShareSurveyModalContents = ({ surveyId }: { surveyId: string }) => {
	const surveyLink = useSurveyLink();
	const setters = useSetters();

	const canEmailSurvey = useSurveyAccessTier(surveyId).tier === "edit";

	const [emailWasSent, setEmailWasSent] = useState(false);
	const { emailScheduled, eligibleParticipants, refetchSurveyEmailSchedule, refetchEligibleParticipants } =
		useSurveyEmailStatus(surveyId);

	const noOneEligible = eligibleParticipants?.length === 0;

	useEffect(() => {
		refetchSurveyEmailSchedule();
		refetchEligibleParticipants();

		// Track when email was actually sent
		if (emailScheduled) {
			setEmailWasSent(true);
		}
	}, [emailScheduled]);

	// Only re-enable the button if we're certain there are no eligible participants
	// This prevents the race condition where emailScheduled becomes false before noOneEligible becomes true
	useEffect(() => {
		if (emailWasSent && noOneEligible) {
			// Email was sent and all participants have responded, safe to reset
			setEmailWasSent(false);
		}
	}, [emailWasSent, noOneEligible]);

	const getButtonText = () => {
		if (noOneEligible) {
			return "Email Unavailable";
		} else if (emailScheduled || emailWasSent) {
			return "Email Scheduled";
		} else {
			return "Email Link";
		}
	};
	return (
		<div className="flex w-full flex-col items-center">
			<Typography size="paragraph-1" color="neutral-9" weight="semi-bold">
				Share this survey!
			</Typography>
			<Typography size="paragraph-2" color="neutral-9" weight="regular">
				<span className="block w-full text-center">
					{canEmailSurvey
						? "You can share the link manually, or distribute to participants via email."
						: "You can share the link below with participants."}
				</span>
			</Typography>
			<Spacer height={3} />
			{canEmailSurvey && noOneEligible && (
				<>
					<Alert
						type="error"
						message="All available employees have already received an email or filled out the survey"
					/>
					<Spacer height={2} />
				</>
			)}
			<div className="flex items-center gap-2">
				<Tooltip
					message="Survey Link Copied"
					openOnClick
					hasArrow={false}
					turnOffAfter={2000}
					placement="bottom"
				>
					<CopyToClipboard text={surveyLink}>
						<div className="flex h-12 items-center">
							<ButtonStandard variant="outline" size="large" color="neutral" iconLeft={faLinkSimple}>
								Copy Link
							</ButtonStandard>
						</div>
					</CopyToClipboard>
				</Tooltip>

				{canEmailSurvey && (
					<ButtonStandard
						variant="solid"
						size="large"
						color="primary"
						iconLeft={faEnvelope}
						disabled={emailScheduled || emailWasSent || noOneEligible}
						onClick={() => {
							setters.setModal(SURVEY_DETAILS_MODALS.SHARE_SURVEY_CONFIRMATION);
						}}
					>
						{getButtonText()}
					</ButtonStandard>
				)}
			</div>
		</div>
	);
};

export const ShareSurveyModal = ({ surveyId }: { surveyId: string }) => {
	const modal = useModal();
	const setters = useSetters();
	const { refetchSurveyEmailSchedule, refetchEligibleParticipants } = useSurveyEmailStatus(surveyId);

	const [modalContents, setModalContents] = useState<ReactNode>(<ShareSurveyModalContents surveyId={surveyId} />);

	useEffect(() => {
		if (modal === SURVEY_DETAILS_MODALS.SHARE_SURVEY) {
			refetchSurveyEmailSchedule();
			refetchEligibleParticipants();
			setModalContents(<ShareSurveyModalContents surveyId={surveyId} />);
		} else if (modal === SURVEY_DETAILS_MODALS.SHARE_SURVEY_CONFIRMATION) {
			setModalContents(<ShareSurveyConfirmationModalContents surveyId={surveyId} />);
		}
	}, [modal]);

	return (
		<ContentModal
			size="small"
			isOpen={
				modal === SURVEY_DETAILS_MODALS.SHARE_SURVEY ||
				modal === SURVEY_DETAILS_MODALS.SHARE_SURVEY_CONFIRMATION
			}
			handleClose={() => setters.setModal(null)}
		>
			{modalContents}
		</ContentModal>
	);
};
